import Big from 'big.js'
import {
  MODOS_NO_MOTORIZADOS,
  MODOS_PAGADOS,
  MODOS_SIN_COSTO,
  NIVELES_CON_REPRESENTANTE,
  RANGO_EDAD_POR_NIVEL,
  VELOCIDAD_MAXIMA,
  type Modo,
  type Moneda,
  type Nivel,
  type RangoDuracion,
} from './catalogos.js'
import {
  aMinutos,
  aUsd,
  diasMes as calcDiasMes,
  diferenciaRelativa,
  indiceRango,
  minutosEntre,
  rangoDuracion,
  FACTOR_MES_DEFECTO,
} from './calculos.js'
import { pareceDireccionExacta } from './privacidad.js'

/**
 * Motor de calidad. Cada regla devuelve hallazgos con un código estable
 * (se guardan en `banderas_calidad`), el campo afectado y un mensaje en
 * lenguaje claro para el estudiante o el supervisor.
 *
 * Severidades:
 *  - error:       bloquea el envío (dato imposible o requisito legal).
 *  - critica:     se acepta pero marca 🔴 y va a revisión.
 *  - advertencia: se acepta con confirmación y marca 🟡.
 *  - info:        solo informativo; no cambia el semáforo.
 */

export type Severidad = 'error' | 'critica' | 'advertencia' | 'info'
export type Semaforo = 'verde' | 'amarillo' | 'rojo'

export interface Hallazgo {
  codigo: string
  campo: string
  severidad: Severidad
  mensaje: string
  detalle?: Record<string, unknown>
}

export interface Umbrales {
  factorMes: string
  toleranciaGastoMensual: number
  toleranciaGastoMensualCritica: number
  toleranciaDiasMes: number
  toleranciaTramosMin: number
  toleranciaTramosGasto: number
  entrevistaMinSeg: number
  gpsMaxKm: number
  horaInusualHasta: string
  estanciaMaximaHoras: number
}

export const UMBRALES_DEFECTO: Umbrales = {
  factorMes: FACTOR_MES_DEFECTO,
  toleranciaGastoMensual: 0.25,
  toleranciaGastoMensualCritica: 0.6,
  toleranciaDiasMes: 0.2,
  toleranciaTramosMin: 15,
  toleranciaTramosGasto: 0.2,
  entrevistaMinSeg: 90,
  gpsMaxKm: 2,
  horaInusualHasta: '04:00',
  estanciaMaximaHoras: 16,
}

type MontoM = { monto: string; moneda: Moneda }

/** Forma mínima que necesita el validador (sirve para borradores parciales). */
export interface DatosValidables {
  modoCaptura?: 'autoaplicada_qr' | 'asistida'
  informante?: 'estudiante' | 'representante'
  parentesco?: string
  asentimiento?: boolean
  edad?: number
  nivel?: Nivel
  nivelOtro?: string
  lugarInicio?: string
  lugarInicioOtro?: string
  origen?: { sectorTexto?: string; metodo?: string }
  diasSemana?: number
  horaSalidaIda?: string
  horaLlegadaIda?: string
  horaSalidaRegreso?: string
  horaLlegadaRegreso?: string
  modoIda?: Modo
  modoIdaOtro?: string
  modoRegreso?: Modo
  modoRegresoOtro?: string
  tramos?: { sentido: 'ida' | 'regreso'; orden: number; modo: Modo; minutos: number; gasto?: MontoM }[]
  gastoIda?: MontoM
  gastoRegreso?: MontoM
  declarados?: { rangoIda?: RangoDuracion; rangoRegreso?: RangoDuracion; diasMes?: number; gastoMensual?: MontoM }
  dificultadPrincipal?: string
  dificultadOtro?: string
  dificultadesOtras?: string[]
  captura?: { inicio?: string; fin?: string; gps?: { lat: number; lng: number; precision: number } }
}

export interface ContextoValidacion {
  umbrales?: Partial<Umbrales>
  /** Tasa Bs/USD para comparar montos en monedas distintas. */
  tasa?: string | null
  /** Distancia origen-institución en km (servidor). */
  distanciaKm?: number | null
  /** Distancia GPS de captura ↔ plantel en km (servidor). */
  distanciaGpsPlantelKm?: number | null
}

const vacio = (s?: string) => !s || s.trim() === ''

function h(codigo: string, campo: string, severidad: Severidad, mensaje: string, detalle?: Record<string, unknown>): Hallazgo {
  return detalle ? { codigo, campo, severidad, mensaje, detalle } : { codigo, campo, severidad, mensaje }
}

function familiaVelocidad(modo: Modo): keyof typeof VELOCIDAD_MAXIMA {
  if (modo === 'a_pie') return 'a_pie'
  if (modo === 'bicicleta') return 'bicicleta'
  return 'motorizado'
}

function montoUsd(m: MontoM | undefined, tasa: string | null | undefined): string | null {
  if (!m) return null
  return aUsd(m.monto, m.moneda, tasa ?? null)
}

export function validar(d: DatosValidables, ctx: ContextoValidacion = {}): Hallazgo[] {
  const u: Umbrales = { ...UMBRALES_DEFECTO, ...ctx.umbrales }
  const out: Hallazgo[] = []

  // ── Informante y consentimiento (D-005) ───────────────────────────────
  if (d.nivel && NIVELES_CON_REPRESENTANTE.includes(d.nivel) && d.informante && d.informante !== 'representante') {
    out.push(h('REPRESENTANTE_REQUERIDO', 'informante', 'error',
      'En educación inicial y primaria debe responder el representante.'))
  }
  if (d.informante === 'representante' && vacio(d.parentesco)) {
    out.push(h('PARENTESCO_REQUERIDO', 'parentesco', 'error', 'Indique el parentesco del representante.'))
  }
  if (d.informante === 'estudiante' && d.edad !== undefined && d.edad < 18 && d.asentimiento !== true) {
    out.push(h('ASENTIMIENTO_REQUERIDO', 'asentimiento', 'error',
      'Como eres menor de edad, necesitamos que confirmes que quieres participar.'))
  }

  // ── "Otro" debe especificarse ─────────────────────────────────────────
  const otros: [string | undefined, string | undefined, string][] = [
    [d.nivel, d.nivelOtro, 'nivelOtro'],
    [d.lugarInicio, d.lugarInicioOtro, 'lugarInicioOtro'],
    [d.modoIda, d.modoIdaOtro, 'modoIdaOtro'],
    [d.modoRegreso, d.modoRegresoOtro, 'modoRegresoOtro'],
    [d.dificultadPrincipal, d.dificultadOtro, 'dificultadOtro'],
  ]
  for (const [valor, texto, campo] of otros) {
    if (valor === 'otro' && vacio(texto)) out.push(h('OTRO_ESPECIFICAR', campo, 'error', 'Especifique la opción «Otro».'))
  }

  // ── Edad vs. nivel ────────────────────────────────────────────────────
  if (d.edad !== undefined && d.nivel) {
    const r = RANGO_EDAD_POR_NIVEL[d.nivel]
    if (r && (d.edad < r[0] || d.edad > r[1])) {
      out.push(h('EDAD_NIVEL', 'edad', 'advertencia',
        `La edad ${d.edad} es poco común para este nivel (esperado ${r[0]}–${r[1]} años). Confirme que es correcta.`,
        { esperado: r }))
    }
  }

  // ── Horas ────────────────────────────────────────────────────────────
  let durIda: number | null = null
  let durRegreso: number | null = null
  if (d.horaSalidaIda && d.horaLlegadaIda) {
    durIda = minutosEntre(d.horaSalidaIda, d.horaLlegadaIda)
    if (durIda === null) {
      out.push(h('HORA_IMPOSIBLE', 'horaLlegadaIda', 'error',
        'La hora de llegada no es coherente con la hora de salida (el viaje no puede durar más de 4 horas).'))
    }
  }
  if (d.horaSalidaRegreso && d.horaLlegadaRegreso) {
    durRegreso = minutosEntre(d.horaSalidaRegreso, d.horaLlegadaRegreso)
    if (durRegreso === null) {
      out.push(h('HORA_IMPOSIBLE', 'horaLlegadaRegreso', 'error',
        'La hora de llegada de regreso no es coherente con la hora de salida de la institución.'))
    }
  }
  if (d.horaLlegadaIda && d.horaSalidaRegreso) {
    const estancia = (aMinutos(d.horaSalidaRegreso) - aMinutos(d.horaLlegadaIda) + 1440) % 1440
    if (estancia === 0 || estancia > u.estanciaMaximaHoras * 60) {
      out.push(h('REGRESO_ANTES_LLEGADA', 'horaSalidaRegreso', 'error',
        'La hora de salida de la institución debe ser posterior a la hora de llegada.'))
    }
  }
  if (d.horaSalidaIda && aMinutos(d.horaSalidaIda) < aMinutos(u.horaInusualHasta)) {
    out.push(h('HORA_INUSUAL', 'horaSalidaIda', 'advertencia',
      `Salir antes de las ${u.horaInusualHasta} es poco común. Confirme la hora (use a. m. / p. m.).`))
  }

  // ── Días ─────────────────────────────────────────────────────────────
  if (d.diasSemana !== undefined && d.diasSemana >= 6 && d.nivel && NIVELES_CON_REPRESENTANTE.includes(d.nivel)) {
    out.push(h('DIAS_NIVEL', 'diasSemana', 'advertencia',
      `${d.diasSemana} días por semana es poco común en inicial o primaria.`))
  }

  // ── Gasto vs. modo ───────────────────────────────────────────────────
  const tramosDe = (s: 'ida' | 'regreso') => (d.tramos ?? []).filter((t) => t.sentido === s)
  const revisarGastoModo = (gasto: MontoM | undefined, modo: Modo | undefined, sentido: 'ida' | 'regreso') => {
    if (!gasto || !modo) return
    const cero = new Big(gasto.monto || '0').eq(0)
    const campo = sentido === 'ida' ? 'gastoIda' : 'gastoRegreso'
    const tramos = tramosDe(sentido)
    if (cero && MODOS_PAGADOS.includes(modo)) {
      out.push(h('GASTO_MODO', campo, 'advertencia',
        'Indicó gasto 0 con un transporte que normalmente se paga. Confirme si viaja gratis.'))
    }
    if (!cero && MODOS_SIN_COSTO.includes(modo) && tramos.every((t) => MODOS_SIN_COSTO.includes(t.modo))) {
      out.push(h('GASTO_MODO', campo, 'advertencia',
        'Indicó un gasto pero su modo principal no tiene costo (a pie o bicicleta). Confirme el monto.'))
    }
  }
  revisarGastoModo(d.gastoIda, d.modoIda, 'ida')
  revisarGastoModo(d.gastoRegreso, d.modoRegreso, 'regreso')

  // ── Tramos (viaje multimodal) ─────────────────────────────────────────
  for (const sentido of ['ida', 'regreso'] as const) {
    const tramos = tramosDe(sentido)
    if (tramos.length === 0) continue
    const ordenes = tramos.map((t) => t.orden).sort((a, b) => a - b)
    if (ordenes.some((o, i) => o !== i + 1)) {
      out.push(h('TRAMOS_ORDEN', `tramos.${sentido}`, 'error', 'Los tramos deben numerarse 1, 2, 3… sin saltos.'))
    }
    const dur = sentido === 'ida' ? durIda : durRegreso
    const sumaMin = tramos.reduce((a, t) => a + t.minutos, 0)
    if (dur !== null && Math.abs(sumaMin - dur) > u.toleranciaTramosMin) {
      out.push(h('TRAMOS_SUMA', `tramos.${sentido}`, 'advertencia',
        `Los tramos suman ${sumaMin} min pero el viaje dura ${dur} min.`, { sumaMin, duracion: dur }))
    }
    const total = sentido === 'ida' ? d.gastoIda : d.gastoRegreso
    const conGasto = tramos.filter((t) => t.gasto)
    if (total && conGasto.length > 0) {
      const usdTramos = conGasto.map((t) => montoUsd(t.gasto, ctx.tasa))
      const usdTotal = montoUsd(total, ctx.tasa)
      if (usdTotal !== null && usdTramos.every((x) => x !== null)) {
        const suma = usdTramos.reduce((a, x) => a.plus(x!), new Big(0)).toFixed(4)
        if (diferenciaRelativa(suma, usdTotal) > u.toleranciaTramosGasto) {
          out.push(h('TRAMOS_SUMA', `tramos.${sentido}`, 'advertencia',
            'La suma del gasto por tramo no coincide con el gasto total del viaje.', { sumaUsd: suma, totalUsd: usdTotal }))
        }
      }
    }
  }

  // ── Declarados vs. calculados (solo validación de coherencia) ─────────
  const decl = d.declarados ?? {}
  if (decl.rangoIda && durIda !== null && Math.abs(indiceRango(decl.rangoIda) - indiceRango(rangoDuracion(durIda))) > 1) {
    out.push(h('DURACION_DECLARADA', 'declarados.rangoIda', 'advertencia',
      'El tiempo de ida que indicó no coincide con las horas de salida y llegada.'))
  }
  if (decl.rangoRegreso && durRegreso !== null &&
      Math.abs(indiceRango(decl.rangoRegreso) - indiceRango(rangoDuracion(durRegreso))) > 1) {
    out.push(h('DURACION_DECLARADA', 'declarados.rangoRegreso', 'advertencia',
      'El tiempo de regreso que indicó no coincide con las horas.'))
  }
  if (decl.diasMes !== undefined && d.diasSemana !== undefined) {
    const calc = calcDiasMes(d.diasSemana, u.factorMes)
    if (diferenciaRelativa(String(decl.diasMes), calc) > u.toleranciaDiasMes) {
      out.push(h('DIAS_MES_DECLARADO', 'declarados.diasMes', 'advertencia',
        `Indicó ${decl.diasMes} días al mes; con ${d.diasSemana} días por semana serían unos ${Math.round(Number(calc))}.`))
    }
  }
  if (decl.gastoMensual && d.gastoIda && d.gastoRegreso && d.diasSemana !== undefined) {
    const ida = montoUsd(d.gastoIda, ctx.tasa)
    const reg = montoUsd(d.gastoRegreso, ctx.tasa)
    const declUsd = montoUsd(decl.gastoMensual, ctx.tasa)
    if (ida !== null && reg !== null && declUsd !== null) {
      const calc = new Big(ida).plus(reg).times(calcDiasMes(d.diasSemana, u.factorMes)).toFixed(4)
      const dif = diferenciaRelativa(declUsd, calc)
      if (dif > u.toleranciaGastoMensual) {
        out.push(h('GASTO_INCOHERENTE', 'declarados.gastoMensual',
          dif > u.toleranciaGastoMensualCritica ? 'critica' : 'advertencia',
          'El gasto mensual que indicó no coincide con el gasto diario multiplicado por los días del mes.',
          { declaradoUsd: declUsd, calculadoUsd: calc, diferencia: dif }))
      }
    }
  }

  // ── Privacidad ───────────────────────────────────────────────────────
  if (d.origen?.sectorTexto && pareceDireccionExacta(d.origen.sectorTexto)) {
    out.push(h('DIRECCION_EXACTA', 'origen.sectorTexto', 'advertencia',
      'Parece una dirección exacta. Por su privacidad, indique solo el sector, barrio o urbanización.'))
  }
  if (d.origen?.metodo === 'texto') {
    out.push(h('ORIGEN_APROXIMADO', 'origen', 'info',
      'El origen se ubicará en el centro de la parroquia (sin mapa ni sector de la lista).'))
  }

  // ── Dificultades ─────────────────────────────────────────────────────
  const otras = d.dificultadesOtras ?? []
  if (d.dificultadPrincipal === 'ninguna' && otras.length > 0) {
    out.push(h('DIFICULTAD_INCOHERENTE', 'dificultadesOtras', 'error',
      'Indicó que no tiene dificultades; no puede marcar dificultades adicionales.'))
  }
  if (d.dificultadPrincipal && otras.includes(d.dificultadPrincipal as never)) {
    out.push(h('DIFICULTAD_INCOHERENTE', 'dificultadesOtras', 'error', 'La dificultad principal no puede repetirse.'))
  }
  if (otras.includes('ninguna' as never)) {
    out.push(h('DIFICULTAD_INCOHERENTE', 'dificultadesOtras', 'error', '«No tengo dificultades» no es una dificultad adicional.'))
  }

  // ── Reglas que dependen de datos del servidor ─────────────────────────
  if (ctx.distanciaKm != null && durIda && d.modoIda) {
    const v = ctx.distanciaKm / (durIda / 60)
    const max = VELOCIDAD_MAXIMA[familiaVelocidad(d.modoIda)]
    if (v > max) {
      out.push(h('VELOCIDAD', 'horaLlegadaIda', 'advertencia',
        `La velocidad implícita (${v.toFixed(1)} km/h) es muy alta para el modo indicado.`, { kmh: v, maximo: max }))
    } else if (v < 1 && ctx.distanciaKm > 2) {
      out.push(h('VELOCIDAD', 'horaLlegadaIda', 'advertencia',
        `La velocidad implícita (${v.toFixed(1)} km/h) es demasiado baja para la distancia.`, { kmh: v }))
    }
    if (MODOS_NO_MOTORIZADOS.includes(d.modoIda) && ctx.distanciaKm > 15) {
      out.push(h('VELOCIDAD', 'modoIda', 'advertencia',
        `Más de 15 km a pie o en bicicleta es poco común (${ctx.distanciaKm.toFixed(1)} km).`))
    }
  }
  if (d.modoCaptura === 'asistida' && d.captura?.inicio && d.captura?.fin) {
    const seg = (Date.parse(d.captura.fin) - Date.parse(d.captura.inicio)) / 1000
    if (seg < u.entrevistaMinSeg) {
      out.push(h('ENTREVISTA_CORTA', 'captura', 'critica',
        `La entrevista duró ${Math.round(seg)} s (mínimo esperado ${u.entrevistaMinSeg} s).`, { segundos: seg }))
    }
  }
  if (d.modoCaptura === 'asistida' && ctx.distanciaGpsPlantelKm != null && ctx.distanciaGpsPlantelKm > u.gpsMaxKm) {
    out.push(h('GPS_LEJOS', 'captura.gps', 'advertencia',
      `La encuesta se capturó a ${ctx.distanciaGpsPlantelKm.toFixed(1)} km del plantel.`))
  }
  if (d.modoCaptura === 'asistida' && d.captura && !d.captura.gps) {
    out.push(h('GPS_AUSENTE', 'captura.gps', 'info', 'La encuesta no tiene GPS de captura.'))
  }

  return out
}

export function semaforo(hallazgos: readonly Pick<Hallazgo, 'severidad'>[]): Semaforo {
  if (hallazgos.some((x) => x.severidad === 'error' || x.severidad === 'critica')) return 'rojo'
  if (hallazgos.some((x) => x.severidad === 'advertencia')) return 'amarillo'
  return 'verde'
}

export function errores(hallazgos: readonly Hallazgo[]): Hallazgo[] {
  return hallazgos.filter((x) => x.severidad === 'error')
}

/**
 * Clave canónica para detectar duplicados. Cada plataforma aplica SHA-256
 * sobre este texto (Web Crypto, Node crypto, Dart crypto).
 */
export function claveDuplicado(d: {
  institucionId?: string
  edad: number
  nivel: string
  zona: string
  horaSalidaIda: string
  modoIda: string
  nombreNormalizado?: string
}): string {
  return [d.institucionId ?? 'nueva', d.edad, d.nivel, d.zona, d.horaSalidaIda, d.modoIda, d.nombreNormalizado ?? '']
    .join('|')
}
